/*
 * The hubs, as data (docs/UI_HUBS_AND_GESTURES_PLAN.md 1.1 and 1.2).
 *
 * The one source for the menus (components/Layout.js), the hub pages
 * (pages/hubs/HubPage.js), the route guards (components/RequireScope.js),
 * quick search (./search.js) and, later, the swipe chain. It replaced
 * utils/navGroups.js, whose three task menus became four hubs.
 *
 * A gate is
 *   null            any signed-in account (a `public` one never reaches the
 *                   staff shell: App.js's PrivateRoute sends it to /survey);
 *   'scope'         one scope of utils/permissions.json;
 *   {any: [...]}    at least one of these scopes;
 *   {all: [...]}    every one of them.
 *
 * Strict hubs (decision D19 / P1): a page listed in a hub opens only for
 * someone who passes the hub's gate AND the page's own. A page listed in two
 * hubs opens if either listing lets the person in. Pages no hub lists are in
 * ROUTE_GATES; an address in neither is the catch-all's business (NotFound).
 *
 * Hiding a link or a page is a convenience, never the authorisation: the
 * Lambda routes check every request.
 *
 * Labels are i18n keys with their namespace (`nav:` for a page, `hubs:` for a
 * hub or a section); a tile's one-line description is `hubs:desc.<item key>`.
 */
import { can } from '../permissions';

const SPECIMEN_READERS = { any: ['specimen:read', 'specimen:register'] };
// The analysis workbench opens for anyone one of its modes lets in (culture and thermal lab:read,
// microscopy game:play); written out here so the menus never load the workbench's code.
export const WORKBENCH_GATE = Object.freeze({ any: ['lab:read', 'game:play'] });
const EVENT_PLAYERS = { any: ['event:play', 'event:organise', 'event:read'] };

export const HOME = { key: 'home', path: '/', chain: 0 };

export const HUBS = [
  {
    key: 'bank',
    path: '/hubs/bank',
    label: 'hubs:bank.title',
    gate: null,
    chain: 1,
    sections: [
      {
        key: 'records',
        label: 'hubs:bank.sections.records',
        items: [
          { key: 'collections', to: '/collections', label: 'nav:collections', gate: null },
          // Also where public sightings are reviewed (the old /public-sightings page).
          { key: 'map', to: '/map', label: 'nav:nationalMap', gate: null, aliases: ['GPS'] },
          // Finds collectors hand to the lab, a pickup or a drop-off (migration 0097).
          { key: 'handoffs', to: '/handoffs', label: 'nav:handoffs', gate: { any: ['handoff:work', 'handoff:read'] } },
          { key: 'library', to: '/library', label: 'nav:library', gate: 'library:read' },
        ],
      },
      {
        key: 'knowledge',
        label: 'hubs:bank.sections.knowledge',
        items: [
          // What the literature credits the bank's isolates' taxa with (spec 25).
          { key: 'bankCapabilities', to: '/bank/capabilities', label: 'nav:bankCapabilities', gate: SPECIMEN_READERS },
          // The dictionary's tree with the bank's counts, never a rarity (spec 25a).
          { key: 'bankTaxonomy', to: '/bank/taxonomy', label: 'nav:bankTaxonomy', gate: SPECIMEN_READERS },
        ],
      },
      {
        key: 'data',
        label: 'hubs:bank.sections.data',
        items: [
          { key: 'import', to: '/import', label: 'nav:import', gate: 'import:run', aliases: ['Excel', 'CSV', 'PITAX'] },
        ],
      },
    ],
  },
  {
    key: 'experiments',
    path: '/hubs/experiments',
    label: 'hubs:experiments.title',
    gate: null,
    chain: 2,
    sections: [
      {
        key: 'research',
        label: 'hubs:experiments.sections.research',
        items: [
          { key: 'protocols', to: '/protocols', label: 'nav:protocols', gate: 'protocol:read' },
          { key: 'experiments', to: '/experiments', label: 'nav:experiments', gate: 'experiment:read' },
        ],
      },
      {
        key: 'quality',
        label: 'hubs:experiments.sections.quality',
        items: [
          { key: 'quality', to: '/quality', label: 'nav:quality', gate: 'iso:read', aliases: ['ISO', 'audit'] },
        ],
      },
      {
        key: 'bench',
        label: 'hubs:experiments.sections.bench',
        items: [
          // The unified analysis workbench (docs/ANALYSIS_WORKBENCH.md): culture, thermal (the
          // thermal station, each ROI tied to an isolate or a plate) and spores (microscopy; spore
          // analysis). Its gate is anyone one of its modes lets in (= the workbench's modes.js
          // WORKBENCH_GATE, pinned there); each mode checks its own.
          { key: 'analysisWorkbench', to: '/tools/analysis', label: 'nav:analysisWorkbench',
            gate: WORKBENCH_GATE,
            aliases: ['culture', 'petri', 'dish', 'colony', 'thermal', 'thermal station', 'FLIR', 'spores',
                      'spore analysis', 'microscope', 'micrograph', 'ROI', 'תרבית', 'פטרי', 'צלחת', 'תרמי',
                      'תחנה תרמית', 'נבגים', 'ניתוח נבגים', 'מיקרוסקופ'] },
          { key: 'scanStation', to: '/scan-station', label: 'nav:scanStation', gate: 'lab:read', aliases: ['QR', 'barcode'] },
        ],
      },
      {
        key: 'systems',
        label: 'hubs:experiments.sections.systems',
        items: [
          // Also where lab systems are paired and listed (the old /links page).
          { key: 'bioreactor', to: '/bioreactor', label: 'nav:bioreactor', gate: 'bioreactor:read' },
        ],
      },
    ],
  },
  {
    key: 'ai',
    path: '/hubs/ai',
    label: 'hubs:ai.title',
    gate: 'ai:read',
    sections: [
      {
        key: 'identify',
        label: 'hubs:ai.sections.identify',
        items: [
          { key: 'identify', to: '/identify', label: 'nav:identify', gate: 'ai:read', aliases: ['AI'] },
          { key: 'modelVerification', to: '/tools/verification', label: 'nav:modelVerification', gate: 'ai:read', aliases: ['AI'] },
        ],
      },
      {
        key: 'review',
        label: 'hubs:ai.sections.review',
        items: [
          { key: 'reviewQueue', to: '/ai/review', label: 'nav:reviewQueue', gate: 'ai:read', aliases: ['AI'] },
          { key: 'gameReview', to: '/game/review', label: 'nav:gameReview', gate: 'game:read' },
        ],
      },
      {
        key: 'tagging',
        label: 'hubs:ai.sections.tagging',
        items: [
          { key: 'annotationWorkbench', to: '/tools/annotation', label: 'nav:annotationWorkbench', gate: 'ai:read' },
        ],
      },
    ],
  },
  {
    key: 'admin',
    path: '/hubs/admin',
    label: 'hubs:admin.title',
    gate: 'ops:read',
    sections: [
      {
        key: 'field',
        label: 'hubs:admin.sections.field',
        items: [
          { key: 'missionCurate', to: '/game/missions/curate', label: 'nav:missionCurate', gate: 'mission:read' },
          { key: 'events', to: '/events', label: 'nav:events', gate: 'event:read' },
        ],
      },
      {
        key: 'people',
        label: 'hubs:admin.sections.people',
        items: [
          // Also where pending public sign-ups are activated.
          { key: 'users', to: '/users', label: 'nav:users', gate: 'users:read' },
          { key: 'activity', to: '/activity', label: 'nav:activity', gate: 'activity:read' },
          // Every sign-in attempt, successful or failed (migration 0074).
          { key: 'loginAudit', to: '/admin/logins', label: 'nav:loginAudit', gate: 'login_audit:read' },
        ],
      },
      {
        key: 'lab',
        label: 'hubs:admin.sections.lab',
        items: [
          // Who each culture-pipeline stage's tasks go to (migration 0076).
          { key: 'pipelineOwners', to: '/admin/pipeline-owners', label: 'nav:pipelineOwners', gate: 'pipeline:read' },
          // Who a new specimen handoff request is emailed to (migration 0097).
          { key: 'handoffOwners', to: '/admin/handoff-owners', label: 'nav:handoffOwners', gate: 'handoff:read' },
        ],
      },
      {
        key: 'system',
        label: 'hubs:admin.sections.system',
        items: [
          // What people reported from the Report issue button.
          { key: 'issueReports', to: '/admin/issues', label: 'nav:issueReports', gate: 'issue:read' },
          // The bank photographs Home's gallery shows (migration 0089).
          { key: 'homeSlides', to: '/admin/home-slides', label: 'nav:homeSlides', gate: 'home:read' },
          // Lab records shown to every account as cells and months (public portal 2c, 0109).
          { key: 'publishedRecords', to: '/admin/published-records', label: 'nav:publishedRecords', gate: 'home:read',
            aliases: ['publish', 'public map'] },
          // The AWS account's spend and its functions' load (pitriot-usage-collector).
          { key: 'costsUsage', to: '/admin/costs-and-usage', label: 'nav:costsUsage', gate: 'costs:read', aliases: ['AWS', 'billing'] },
          { key: 'mlops', to: '/mlops', label: 'nav:mlops', gate: 'mlops:read', aliases: ['models', 'training'] },
          // Making the simulated CAPTCHA pass or fail, and checking the real one (0107).
          { key: 'testModes', to: '/admin/test-modes', label: 'nav:testModes', gate: 'testmode:manage',
            aliases: ['CAPTCHA', 'Turnstile'] },
        ],
      },
    ],
  },
];

/*
 * Routed pages no hub lists, and who may open them. The lab pages are reached
 * from Home (the isolates hub) and by a code lookup, never from a menu, and
 * each checks its records' visibility itself.
 */
export const ROUTE_GATES = {
  '/': null,
  '/account': null,
  // Players reach missions and their events from Field Mode's Tasks sheet
  // (components/field/FieldTasksSheet.js, plan phase 6); curators from the
  // Admin hub's Field command.
  '/game/missions': 'mission:play',
  // The microscope game (Stage 4): desk missions' "annotate slides" lead here, and the analysis
  // workbench's microscopy mode links to it; spore analysis itself is the workbench's.
  '/game/microscope': 'game:play',
  '/events/:missionId': EVENT_PLAYERS,
  '/chat': 'chat:read',
  '/plates': null,
  '/slants': null,
  '/strains': null,
  '/specimens': null,
  '/specimens/:specimenId': null,
  '/lab': null,
  '/dna': null,
};

/** Every scope a gate names (for checking them all, whatever the roles). */
export function gateScopes(gate) {
  if (gate === null || gate === undefined) return [];
  if (typeof gate === 'string') return [gate];
  return [...(gate.any || []), ...(gate.all || [])];
}

export function gateAllows(user, gate) {
  if (gate === null || gate === undefined) return true;
  if (typeof gate === 'string') return can(user, gate);
  if (Array.isArray(gate.any)) return gate.any.some((scope) => can(user, scope));
  if (Array.isArray(gate.all)) return gate.all.every((scope) => can(user, scope));
  throw new Error(`unknown gate: ${JSON.stringify(gate)}`);
}

/** The address without its query, hash or a trailing slash. */
export function cleanPath(pathname) {
  const path = String(pathname || '/').split(/[?#]/)[0] || '/';
  return path.length > 1 ? path.replace(/\/+$/, '') || '/' : path;
}

/** A route pattern (':param' segments) against an address. */
export function matchesPattern(pattern, pathname) {
  if (pattern === pathname) return true;
  const want = pattern.split('/');
  const got = pathname.split('/');
  if (want.length !== got.length) return false;
  return want.every((part, i) => (part.startsWith(':') ? got[i] !== '' : part === got[i]));
}

/*
 * The hubs a person may open, with the sections and pages they may follow: a
 * hub's gate AND each page's. A section with nothing left in it is dropped,
 * and so is a hub with no page left (never an empty menu or an empty hub).
 */
export function visibleHubs(user) {
  return HUBS
    .filter((hub) => gateAllows(user, hub.gate))
    .map((hub) => ({
      ...hub,
      sections: hub.sections
        .map((section) => ({ ...section, items: section.items.filter((item) => gateAllows(user, item.gate)) }))
        .filter((section) => section.items.length > 0),
    }))
    .filter((hub) => hub.sections.length > 0);
}

const hubItems = (hub) => hub.sections.flatMap((section) => section.items);

/** Whether this person may open the page at `pathname` (strict hubs, D19). */
export function pathAllowed(user, pathname) {
  const path = cleanPath(pathname);
  const page = HUBS.find((hub) => hub.path === path);
  if (page) return visibleHubs(user).some((hub) => hub.key === page.key);
  const listings = HUBS.flatMap((hub) => hubItems(hub)
    .filter((item) => item.to === path)
    .map((item) => ({ hub, item })));
  if (listings.length) {
    return listings.some(({ hub, item }) => gateAllows(user, hub.gate) && gateAllows(user, item.gate));
  }
  const key = Object.keys(ROUTE_GATES).find((pattern) => matchesPattern(pattern, path));
  if (key !== undefined) return gateAllows(user, ROUTE_GATES[key]);
  return true;
}

/** Home, then each visible hub that has a place in the swipe chain, in order. */
export function swipeChain(user) {
  const hubs = visibleHubs(user)
    .filter((hub) => typeof hub.chain === 'number')
    .sort((a, b) => a.chain - b.chain);
  return [HOME, ...hubs];
}

/*
 * The hub that owns the current page, so its menu trigger can show where you
 * are: the hub page itself, else the longest matching page path among the
 * person's visible hubs (/game/missions/curate is curation's, not the
 * missions page's). A page in two hubs belongs to the first. Home, and a lab
 * page reached from it, belong to none.
 */
export function activeHubKey(pathname, user) {
  const path = cleanPath(pathname);
  let best = null;
  let bestLength = -1;
  for (const hub of visibleHubs(user)) {
    if (path === hub.path || path.startsWith(`${hub.path}/`)) return hub.key;
    for (const item of hubItems(hub)) {
      const matches = path === item.to || path.startsWith(`${item.to}/`);
      if (matches && item.to.length > bestLength) {
        best = hub.key;
        bestLength = item.to.length;
      }
    }
  }
  return best;
}

/** The i18n key naming the page at `path` (a hub, or a page a hub lists), else null. */
export function pageLabel(path) {
  const clean = cleanPath(path);
  const hub = HUBS.find((h) => h.path === clean);
  if (hub) return hub.label;
  for (const h of HUBS) {
    const item = hubItems(h).find((i) => i.to === clean);
    if (item) return item.label;
  }
  return null;
}

/** The one-line description of a page's tile on its hub page. */
export const descriptionKey = (item) => `hubs:desc.${item.key}`;
